/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface Strategy {
  /**
   * A strategy is a set of functions of which one is chosen dynamically at runtime for execution
   *
   * @public
   * @function f1.strategy
   * @param strategies
   * @param strategy
   * @example
   *
   * ```ts
   * const directionStrategy = {
   *   t: 'top',
   *   r: 'right',
   *   b: 'bottom',
   *   l: 'left',
   *   fallback: 'top'
   * }
   * f1.strategy(directionStrategy, directionKey);
   * const userPermissionsStrategy = {
   *  advisor: () => {
   *    return getAdvisorPermissions();
   *  },
   *  student: () => {
   *    return getStudentPermissions();
   *  },
   *  superuser: () => {
   *    return getSuperUserPermissions();
   *  },
   *  fallback: () => {
   *    return getStudentPermissions();
   *  }
   * }
   * f1.strategy(userPermissionsStrategy, role);
   * ```
   */
  <S extends string, E>(strategies: { fallback: E } & Record<S, E>, strategy: S): E;
  /**
   * @param strategies
   * @param strategy
   */
  <S extends string, E>(strategies: { fallback: E } & Record<S, E>): (strategy: S) => E;
}

/**
 * @see Strategy
 */
export default <S extends string, E>(strategies: { fallback: E } & Record<S, E>, strategy: S): E => {
  return strategies[strategy] || strategies['fallback'];
};
